/**
 * DealValidationModal
 *
 * Triggered whenever a deal status change is blocked by missing required fields.
 * The user can fill in the missing fields inline and confirm the move — all in a
 * single request.
 *
 * Scalable by design: add new entries to FIELD_REGISTRY to support new field types
 * without touching this component's core rendering logic.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../utils/api'
import { XIcon } from './Icons'

// ── Field registry ────────────────────────────────────────────────────────────

type FieldType =
  | 'currency'
  | 'number'
  | 'text'
  | 'date'
  | 'textarea'
  | 'select-customers'
  | 'lender-search'       // searchable + create-on-the-spot
  | 'select-deal-types'

interface FieldMeta {
  /** i18n key inside dealValidation.fields.* */
  labelKey: string
  type: FieldType
  placeholder?: string
  /** Actual DB column to write when this field key differs from the DB column */
  dbKey?: string
}

const FIELD_REGISTRY: Record<string, FieldMeta> = {
  amount:            { labelKey: 'amount',            type: 'currency',         placeholder: '0.00' },
  customer_id:       { labelKey: 'customer_id',       type: 'select-customers'                       },
  lender_id:         { labelKey: 'lender_id',         type: 'lender-search'                          },
  lender_name:       { labelKey: 'lender_name',       type: 'text',             placeholder: '',      dbKey: 'lender_name' },
  deal_type_id:      { labelKey: 'deal_type_id',      type: 'select-deal-types'                      },
  closing_date:      { labelKey: 'closing_date',      type: 'date'                                    },
  description:       { labelKey: 'description',       type: 'textarea',         placeholder: ''       },
  notes:             { labelKey: 'notes',             type: 'textarea',         placeholder: '',      dbKey: 'description' },
  base_points:       { labelKey: 'base_points',       type: 'number',           placeholder: '0'      },
  bonus_points:      { labelKey: 'bonus_points',      type: 'number',           placeholder: '0'      },
  volume_points:     { labelKey: 'volume_points',     type: 'number',           placeholder: '0'      },
  property_address:  { labelKey: 'property_address',  type: 'text',             placeholder: ''       },
  funded_date:       { labelKey: 'funded_date',       type: 'date'                                    },
  commission_amount: { labelKey: 'commission_amount', type: 'currency',         placeholder: '0.00'   },
}

function getFieldMeta(fieldKey: string): FieldMeta {
  return FIELD_REGISTRY[fieldKey] ?? { labelKey: fieldKey, type: 'text' }
}

// ── Shorthand amount parser (400K → 400000, 2M → 2000000) ────────────────────

function parseShorthand(raw: string): string {
  const s = raw.trim().toUpperCase()
  const m = s.match(/^(\d+\.?\d*)\s*([KMB]?)$/)
  if (!m) return raw
  const n = parseFloat(m[1])
  if (isNaN(n)) return raw
  if (m[2] === 'K') return String(n * 1_000)
  if (m[2] === 'M') return String(n * 1_000_000)
  if (m[2] === 'B') return String(n * 1_000_000_000)
  return raw
}

// Whether a field value counts as "satisfied" (for hiding the asterisk)
function isSatisfied(field: string, val: any): boolean {
  if (val === null || val === undefined || val === '') return false
  if (field === 'amount') {
    const n = parseFloat(String(val))
    return !isNaN(n) && n > 0
  }
  return true
}

// ── Option types ──────────────────────────────────────────────────────────────

interface SelectOption { value: string | number; label: string }

// ── Lender search sub-component (portal dropdown) ─────────────────────────────

interface LenderSearchFieldProps {
  value: number | string | null
  displayName: string
  onChange: (id: number, name: string) => void
}

const LenderSearchField: React.FC<LenderSearchFieldProps> = ({ value, displayName, onChange }) => {
  const { t } = useTranslation()
  const [query, setQuery]       = useState('')
  const [results, setResults]   = useState<SelectOption[]>([])
  const [open, setOpen]         = useState(false)
  const [creating, setCreating] = useState(false)
  const [searching, setSearching] = useState(false)
  const inputRef   = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm'

  // Reposition dropdown to sit right below the input, using fixed coords so it
  // escapes any overflow:hidden / overflow-y-auto ancestor.
  const positionDropdown = useCallback(() => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    })
  }, [])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        inputRef.current && !inputRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Update position if window scrolls or resizes while dropdown is open
  useEffect(() => {
    if (!open) return
    const update = () => positionDropdown()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, positionDropdown])

  const search = async (q: string) => {
    setSearching(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/lenders`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) return
      const data = await res.json()
      const all: SelectOption[] = (data.lenders || []).map((l: any) => ({ value: l.id, label: l.name }))
      setResults(q.trim() ? all.filter(l => l.label.toLowerCase().includes(q.toLowerCase())) : all)
    } finally {
      setSearching(false)
    }
  }

  const handleFocus = () => {
    positionDropdown()
    setOpen(true)
    setQuery('')
    search('')
  }

  const handleInput = (q: string) => {
    setQuery(q)
    search(q)
  }

  const handleSelect = (opt: SelectOption) => {
    onChange(Number(opt.value), String(opt.label))
    setOpen(false)
    setQuery('')
  }

  const handleCreate = async () => {
    if (!query.trim()) return
    setCreating(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE_URL}/api/lenders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: query.trim() })
      })
      if (res.ok) {
        const data = await res.json()
        const lender = data.lender
        onChange(lender.id, lender.name)
        setOpen(false)
        setQuery('')
      }
    } finally {
      setCreating(false)
    }
  }

  const currentLabel = value ? (displayName || String(value)) : ''

  const dropdown = open ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-52 overflow-y-auto"
    >
      {searching ? (
        <div className="px-3 py-2 text-sm text-gray-400">{t('common.loading')}</div>
      ) : results.length > 0 ? (
        results.map(opt => (
          <button
            key={opt.value}
            type="button"
            onMouseDown={e => { e.preventDefault(); handleSelect(opt) }}
            className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            {opt.label}
          </button>
        ))
      ) : null}
      {query.trim() && !results.some(r => r.label.toLowerCase() === query.toLowerCase()) && (
        <button
          type="button"
          onMouseDown={e => { e.preventDefault(); handleCreate() }}
          disabled={creating}
          className="w-full text-left px-3 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-t border-gray-100 dark:border-gray-600 font-medium"
        >
          {creating
            ? t('dealValidation.lender.creating')
            : t('dealValidation.lender.createNew', { name: query.trim() })}
        </button>
      )}
      {!searching && results.length === 0 && !query.trim() && (
        <div className="px-3 py-2 text-sm text-gray-400">{t('dealValidation.lender.noResults')}</div>
      )}
    </div>,
    document.body
  ) : null

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={open ? query : currentLabel}
        onChange={e => handleInput(e.target.value)}
        onFocus={handleFocus}
        placeholder={t('dealValidation.lender.searchPlaceholder')}
        className={inputClass}
        autoComplete="off"
      />
      {dropdown}
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DealValidationModalProps {
  isOpen: boolean
  dealId: number
  dealData: Record<string, any>
  missingFields: string[]
  targetStatusId: number
  targetStatusName: string
  onSuccess: () => void
  onClose: () => void
}

// ── Main component ────────────────────────────────────────────────────────────

const DealValidationModal: React.FC<DealValidationModalProps> = ({
  isOpen,
  dealId,
  dealData,
  missingFields,
  targetStatusId,
  targetStatusName,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation()

  const [values, setValues]           = useState<Record<string, any>>({})
  const [lenderNames, setLenderNames] = useState<Record<string, string>>({})
  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState<string | null>(null)

  const [customers,  setCustomers]  = useState<SelectOption[]>([])
  const [dealTypes,  setDealTypes]  = useState<SelectOption[]>([])

  const needsCustomers = missingFields.includes('customer_id')
  const needsDealTypes = missingFields.includes('deal_type_id')

  // Seed form from existing deal data whenever the modal opens
  useEffect(() => {
    if (!isOpen) return
    const seed:  Record<string, any>    = {}
    const names: Record<string, string> = {}
    for (const field of missingFields) {
      const meta = getFieldMeta(field)
      const sourceKey = meta.dbKey ?? field
      const raw = dealData[sourceKey] ?? dealData[field] ?? ''
      // Don't seed 0 into currency/amount fields — show blank so the user must enter a real value
      if (meta.type === 'currency' && (raw === 0 || raw === '0' || raw === '0.00')) {
        seed[field] = ''
      } else {
        seed[field] = raw
      }
      if (field === 'lender_id' && dealData.lender_name) {
        names[field] = dealData.lender_name
      }
    }
    setValues(seed)
    setLenderNames(names)
    setError(null)
  }, [isOpen, missingFields, dealData])

  // Fetch simple select options
  useEffect(() => {
    if (!isOpen) return
    const token   = localStorage.getItem('token')
    const headers = { Authorization: `Bearer ${token}` }

    if (needsCustomers) {
      fetch(`${API_BASE_URL}/api/customers`, { headers })
        .then(r => r.json())
        .then(d => setCustomers((d.customers || d || []).map((c: any) => ({ value: c.id, label: c.name }))))
        .catch(() => setCustomers([]))
    }
    if (needsDealTypes) {
      fetch(`${API_BASE_URL}/api/deal-types`, { headers })
        .then(r => r.json())
        .then(d => setDealTypes((d.dealTypes || d || []).map((dt: any) => ({ value: dt.id, label: dt.name }))))
        .catch(() => setDealTypes([]))
    }
  }, [isOpen, needsCustomers, needsDealTypes])

  if (!isOpen) return null

  const setValue = (field: string, val: any) => setValues(prev => ({ ...prev, [field]: val }))

  const getFieldLabel = (field: string): string => {
    const meta = getFieldMeta(field)
    return t(`dealValidation.fields.${meta.labelKey}`, { defaultValue: field.replace(/_/g, ' ') })
  }

  const buildPayload = (): Record<string, any> => {
    const payload: Record<string, any> = { status_id: targetStatusId }
    for (const [field, val] of Object.entries(values)) {
      if (val === '' || val === null || val === undefined) continue
      const meta = getFieldMeta(field)
      payload[meta.dbKey ?? field] = val
    }
    return payload
  }

  const handleSaveAndMove = async () => {
    setSaving(true)
    setError(null)
    try {
      const token    = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deals/${dealId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      })
      const data = await response.json()

      if (response.ok) {
        onSuccess()
        onClose()
        return
      }

      if (data.requiresValidation && data.missingFields?.length) {
        const labels = data.missingFields.map((f: string) => getFieldLabel(f)).join(', ')
        setError(`${t('dealValidation.stillMissing')} : ${labels}`)
      } else {
        setError(data.message || data.error || t('dealValidation.errorOccurred'))
      }
    } catch {
      setError(t('dealValidation.errorOccurred'))
    } finally {
      setSaving(false)
    }
  }

  // ── Field renderer ──────────────────────────────────────────────────────────

  const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm'

  const renderField = (field: string) => {
    const meta = getFieldMeta(field)
    const val  = values[field] ?? ''

    switch (meta.type) {
      case 'currency':
        return (
          <div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={val}
                onChange={e => setValue(field, e.target.value)}
                onBlur={e => {
                  const parsed = parseShorthand(e.target.value)
                  setValue(field, parsed)
                }}
                placeholder="e.g. 400K or 2M"
                className={`${inputClass} pl-7`}
              />
            </div>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Supports shorthand: 400K, 2M…</p>
          </div>
        )

      case 'number':
        return (
          <input type="number" min="0" value={val}
            onChange={e => setValue(field, e.target.value)}
            placeholder={meta.placeholder} className={inputClass} />
        )

      case 'date':
        return (
          <input type="date" value={val}
            onChange={e => setValue(field, e.target.value)} className={inputClass} />
        )

      case 'textarea':
        return (
          <textarea value={val} onChange={e => setValue(field, e.target.value)}
            rows={3} className={inputClass} />
        )

      case 'select-customers':
        return (
          <select value={val} onChange={e => setValue(field, e.target.value)} className={inputClass}>
            <option value="">{t('dealValidation.select.client')}</option>
            {customers.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )

      case 'lender-search':
        return (
          <LenderSearchField
            value={val || null}
            displayName={lenderNames[field] || ''}
            onChange={(id, name) => {
              setValue(field, id)
              setLenderNames(prev => ({ ...prev, [field]: name }))
            }}
          />
        )

      case 'select-deal-types':
        return (
          <select value={val} onChange={e => setValue(field, e.target.value)} className={inputClass}>
            <option value="">{t('dealValidation.select.dealType')}</option>
            {dealTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )

      default:
        return (
          <input type="text" value={val} onChange={e => setValue(field, e.target.value)}
            placeholder={meta.placeholder} className={inputClass} />
        )
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      {/* Modal — wider and taller so fields breathe and the dropdown has room */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl flex flex-col"
           style={{ maxHeight: 'min(85vh, 640px)' }}>

        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('dealValidation.title')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('dealValidation.subtitle')}{' '}
                  <strong className="text-gray-700 dark:text-gray-300">"{targetStatusName}"</strong>
                </p>
              </div>
            </div>
            <button onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ml-2 flex-shrink-0">
              <XIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Fields — overflow-visible so the portal dropdown can extend beyond */}
        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-5">
          {missingFields.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">{t('dealValidation.noMissingFields')}</p>
          ) : (
            missingFields.map(field => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  {getFieldLabel(field)}
                  {!isSatisfied(field, values[field]) && (
                    <span className="text-red-500">*</span>
                  )}
                  {isSatisfied(field, values[field]) && (
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </label>
                {renderField(field)}
              </div>
            ))
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
            {t('dealValidation.cancel')}
          </button>
          <button
            onClick={handleSaveAndMove}
            disabled={saving || missingFields.length === 0}
            className="px-6 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 shadow-sm"
          >
            {saving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>{t('dealValidation.saving')}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('dealValidation.saveAndMove')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DealValidationModal
